import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [office, setOffice] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isSetupCompleted, setIsSetupCompleted] = useState(true); // Varsayılan true (login olmadan önce)

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/me');
      setUser(response.data.user);
      setOffice(response.data.user?.office || null);
      setIsSetupCompleted(response.data.user?.office?.is_setup_completed ?? true);

      const permRes = await api.get('/me/permissions');
      setPermissions(permRes.data.data || {});

      setLoading(false);
    } catch (error) {
      console.error('Kullanıcı bilgisi alınamadı:', error);
      // 401 hatası alındığında sessizce logout yap
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setOffice(null);
        setIsSetupCompleted(true);
        delete api.defaults.headers.common['Authorization'];
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/login', { email, password });
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      setOffice(user?.office || null);
      setIsSetupCompleted(user?.office?.is_setup_completed ?? true);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'E-posta adresiniz veya şifreniz yanlış lütfen tekrar deneyin.'
      };
    }
  };

  const register = async (formData) => {
    try {
      const response = await api.post('/register', formData);
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      setOffice(user?.office || null);
      setIsSetupCompleted(false); // Yeni kayıt = kurulum yapılmamış
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      let errorMessage = 'Kayıt işlemi başarısız.';

      if (error.response?.status === 422) {
        // Validation hatalarından ilkini al
        const errors = error.response.data.errors;
        if (errors) {
          const firstKey = Object.keys(errors)[0];
          errorMessage = errors[firstKey][0];
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const logout = async () => {
    // Token varsa logout endpoint'ini çağır, yoksa direkt temizle
    if (token) {
      try {
        await api.post('/logout');
      } catch (error) {
        // 401 hatası normal, token zaten geçersiz
        if (error.response?.status !== 401) {
          console.error('Logout error:', error);
        }
      }
    }

    // Her durumda local state'i temizle
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setOffice(null);
    setIsSetupCompleted(true);
    delete api.defaults.headers.common['Authorization'];
  };

  const refreshSetupStatus = async () => {
    try {
      const response = await api.get('/setup/status');
      const completed = response.data.data?.is_setup_completed ?? false;
      setIsSetupCompleted(completed);
      if (completed) {
        // Kullanıcı bilgilerini de yenile
        await fetchUser();
      }
      return completed;
    } catch (error) {
      console.error('Setup status alınamadı:', error);
      return false;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const response = await api.post('/email/resend');
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Doğrulama e-postası gönderilemedi.'
      };
    }
  };

  const isTrialExpiringSoon = () => {
    if (!office || !office.activeSubscription) return false;
    const sub = office.activeSubscription;
    if (sub.status !== 'trial' || !sub.trial_ends_at) return false;

    const trialEnd = new Date(sub.trial_ends_at);
    const now = new Date();
    const diffDays = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));

    return diffDays >= 0 && diffDays <= 7;
  };

  const getTrialRemainingDays = () => {
    if (!office || !office.activeSubscription) return 0;
    const sub = office.activeSubscription;
    if (sub.status !== 'trial' || !sub.trial_ends_at) return 0;

    const trialEnd = new Date(sub.trial_ends_at);
    const now = new Date();
    const diffDays = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const isPortfolioFullAccess = () => {
    return office?.settings?.default_portfolio_visibility === 'office';
  };

  const value = {
    user,
    office,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isEmailVerified: user?.email_verified_at !== null,
    isSetupCompleted,
    resendVerificationEmail,
    refreshSetupStatus,
    getTrialRemainingDays,
    isPortfolioFullAccess,
    permissions,
    fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
